/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      const slideshow = this,
        dataset = slideshow.dataset;

      const slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));

      // Single early-return guard
      if (slideshow_slides.length < 1) return;

      const dots_enabled = dataset.dots === 'true',
        autoplay = dataset.autoplay == 'false' ? false : parseInt(dataset.autoplay, 10),
        align = dataset.align == 'center' ? 'center' : 'left',
        fade = dataset.fade == 'true',
        custom_dots = slideshow.querySelector('.flickity-page-dots'),
        animations = [],
        rightToLeft = document.dir === 'rtl',
        animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      // Set by the pause button; reduced motion starts paused.
      let autoplay_paused = !!autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Background videos hold still while the slideshow is paused.
      const videoSync = (video_container) => autoplay_paused ? slideshow.videoPause(video_container) : slideshow.videoPlay(video_container);

      // Cache classList checks — avoids repeated DOM reads throughout constructor
      const isImageTextImage   = slideshow.classList.contains('image-with-text-slideshow--image');
      const isImageTextContent = slideshow.classList.contains('image-with-text-slideshow--content');
      const isTestimonials     = slideshow.classList.contains('testimonials__carousel');
      const isCustomDots       = slideshow.classList.contains('custom-dots');
      const isMainSlideshow    = slideshow.classList.contains('main-slideshow');

      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: dots_enabled,
        contain: true,
        fade,
        autoPlay: autoplay,
        rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        on: {}
      };

      if (isImageTextImage) {
        const image_slideshow_slides = slideshow.querySelectorAll('.image-with-text-slideshow--image-media');
        args.draggable = false;
        if (image_slideshow_slides.length && image_slideshow_slides[0].classList.contains('desktop-height-auto')) {
          args.adaptiveHeight = true;
        }
      }
      if (isImageTextContent) {
        args.draggable = false;
        args.asNavFor = slideshow.parentNode.querySelector('.image-with-text-slideshow--image');
      }
      if (isImageTextContent || isTestimonials) {
        args.adaptiveHeight = true;
      }

      // Cache dot elements once — prevents querySelectorAll on every slide change
      const cached_dot_list = custom_dots ? Array.from(custom_dots.querySelectorAll('li')) : null;

      if (isCustomDots) {
        if (animations_enabled && isMainSlideshow) {
          this.prepareAnimations(slideshow, animations);
        }
        args.pauseAutoPlayOnHover = false;

        args.on = {
          ready: function () {
            let flkty = this;

            if (animations_enabled && isMainSlideshow) {
              slideshow.animateSlides(0, animations);
              gsap.set(slideshow.querySelectorAll('.inline-badge,.subheading,.split-text,.button'), { visibility: 'visible' });
            }

            if (cached_dot_list) {
              cached_dot_list.forEach((dot, i) => {
                dot.addEventListener('click', () => flkty.select(i));
              });
              cached_dot_list[this.selectedIndex].classList.add('is-selected');
            }

            document.fonts.ready.then(() => flkty.resize());

            // Video Support.
            const video_container = flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              const iframe = video_container.querySelector('iframe');
              const video = video_container.querySelector('video');
              if (iframe) {
                iframe.onload = () => videoSync(video_container);
              } else if (video) {
                video.onloadstart = () => videoSync(video_container);
              }
            }
          },
          change: function (index) {
            flkty.cells[0].element.classList.remove('is-initial-selected');
            const previousIndex = fizzyUIUtils.modulo(this.selectedIndex - 1, this.slides.length);

            // Animations.
            if (animations_enabled && isMainSlideshow) {
              // gsap.delayedCall is GSAP-native and avoids a bare setTimeout magic number
              gsap.delayedCall(0.7, () => slideshow.animateReverse(previousIndex, animations));
              slideshow.animateSlides(index, animations);
            }

            // Color changes — single getComputedStyle call avoids two forced reflows
            const selectedElement = this.selectedElement;
            const cs = getComputedStyle(selectedElement);
            slideshow.style.setProperty('--color-body', cs.getPropertyValue('--color-body'));
            slideshow.style.setProperty('--color-body-rgb', cs.getPropertyValue('--color-body-rgb'));

            // Custom Dots.
            if (cached_dot_list) {
              cached_dot_list.forEach((dot) => dot.classList.remove('is-selected'));
              cached_dot_list[this.selectedIndex].classList.add('is-selected');
            }

            // AutoPlay
            if (autoplay && !autoplay_paused) {
              this.stopPlayer();
              this.playPlayer();
            }

            // Video Support.
            const video_container_prev = flkty.cells[previousIndex].element.querySelector('.slideshow__slide-video-bg');
            if (video_container_prev) {
              slideshow.videoPause(video_container_prev);
            }

            const video_container = flkty.cells[index].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              // Cache iframe — avoids triple querySelector on the same element
              const iframe = video_container.querySelector('iframe');
              if (iframe) {
                if (iframe.classList.contains('lazyload')) {
                  // The embed autoplays once loaded, so sync it then rather than at unveil.
                  iframe.onload = () => videoSync(video_container);
                  lazySizes.loader.checkElems();
                } else {
                  videoSync(video_container);
                }
              } else if (video_container.querySelector('video')) {
                videoSync(video_container);
              }
            }
          }
        };
      }

      if (isMainSlideshow) {
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }

      const flkty = new Flickity(slideshow, args);

      dataset.initiated = true;

      // Autoplay controls (WCAG 2.2.2).
      if (autoplay) {
        const player = flkty.player,
          play = player.play,
          pause = player.pause,
          stop = player.stop,
          autoplay_toggle = slideshow.querySelector('.carousel__autoplay-toggle'),
          focus_root = isImageTextImage ? slideshow.parentNode : slideshow;

        let hovered = false,
          was_playing = player.state === 'playing';

        // Rotation holds while focus is inside (bar the toggle itself), or the pointer rests on a carousel that pauses on hover.
        const isHeld = (focused = document.activeElement) => (hovered && flkty.options.pauseAutoPlayOnHover) || (focused !== autoplay_toggle && focus_root.contains(focused));

        // Mirror the player's state; the dot's progress bar restarts with every resumed interval.
        const updateAutoplayToggle = () => {
          const state = player.state,
            playing = state === 'playing';
          slideshow.classList.toggle('autoplay-paused', state === 'stopped');
          slideshow.classList.toggle('autoplay-held', state === 'paused');
          if (autoplay_toggle) {
            autoplay_toggle.setAttribute('aria-label', autoplay_toggle.dataset[state === 'stopped' ? 'playLabel' : 'pauseLabel']);
          }
          if (playing && !was_playing) {
            slideshow.querySelectorAll('.flickity-page-dots .dot.is-selected').forEach((dot) => {
              dot.classList.remove('is-selected');
              getComputedStyle(dot, ':after').getPropertyValue('transform'); // Flush, so the bar starts over from empty.
              dot.classList.add('is-selected');
            });
          }
          was_playing = playing;
        };

        const syncSelectedVideo = () => {
          const video_container = flkty.selectedElement.querySelector('.slideshow__slide-video-bg');
          if (video_container) {
            videoSync(video_container);
          }
        };

        // Flickity starts and stops the player itself (hover, tab visibility, drag, dots, arrow keys),
        // so every change runs through here and the pause button and the hold always win.
        player.play = () => {
          if (autoplay_paused) {
            stop.call(player);
          } else {
            play.call(player);
            if (isHeld()) {
              pause.call(player);
            }
          }
          updateAutoplayToggle();
        };
        player.pause = () => {
          pause.call(player);
          updateAutoplayToggle();
        };
        player.stop = () => {
          stop.call(player);
          updateAutoplayToggle();
        };

        // The change handler restarts the player after a drag, so a press alone — a tap,
        // or a scroll that starts on the slideshow — must not stop it for good.
        if (isCustomDots) {
          flkty.off('pointerDown', flkty.stopPlayer);
        }

        if (autoplay_paused) {
          flkty.stopPlayer();
          syncSelectedVideo();
        }
        updateAutoplayToggle();

        if (autoplay_toggle) {
          autoplay_toggle.addEventListener('click', () => {
            autoplay_paused = player.state !== 'stopped';
            // Safari leaves focus on the slideshow, not the button, which would hold a resumed rotation.
            autoplay_toggle.focus();
            if (autoplay_paused) {
              flkty.stopPlayer();
            } else {
              flkty.playPlayer();
            }
            syncSelectedVideo();
          });
          autoplay_toggle.hidden = false;
        }

        // Hold the rotation while focus is inside, so a focused slide never rotates away.
        const hold = (focused) => isHeld(focused) ? flkty.pausePlayer() : flkty.unpausePlayer();
        focus_root.addEventListener('focusin', (event) => hold(event.target));
        focus_root.addEventListener('focusout', (event) => hold(event.relatedTarget));

        // Flickity's own mouseleave listener runs after this one, so its unpause sees the pointer gone.
        slideshow.addEventListener('mouseenter', () => {
          hovered = true;
        });
        slideshow.addEventListener('mouseleave', () => {
          hovered = false;
        });
      }

      if (Shopify.designMode) {
        slideshow.addEventListener('shopify:block:select', (event) => {
          const index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }
    }
    videoPause(video_container) {
      setTimeout(() => {
        if (video_container.dataset.provider === 'hosted') {
          video_container.querySelector('video').pause();
        } else {
          const iframe = video_container.querySelector('iframe');
          const message = iframe.dataset.provider === 'youtube' ? { event: "command", func: "pauseVideo", args: "" } : { method: "pause" };
          iframe.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    videoPlay(video_container) {
      setTimeout(() => {
        if (video_container.dataset.provider === 'hosted') {
          video_container.querySelector('video').play();
        } else {
          const iframe = video_container.querySelector('iframe');
          const message = iframe.dataset.provider === 'youtube' ? { event: "command", func: "playVideo", args: "" } : { method: "play" };
          iframe.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    prepareAnimations(slideshow, animations) {
      if (!slideshow.dataset.animationsReady) {
        // Cache NodeList — reused for both SplitText passes
        const split_text_els = slideshow.querySelectorAll('.split-text');
        new SplitText(split_text_els, { type: 'lines', linesClass: 'line-child' });
        new SplitText(split_text_els, { type: 'lines', linesClass: 'line-parent' });

        slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
          // Cache all element refs per slide — avoids repeated querySelector in timeline definitions
          const bg           = item.querySelector('.slideshow__slide-bg');
          const badge        = item.querySelector('.inline-badge');
          const subheading   = item.querySelector('.subheading');
          const headingLines = item.querySelectorAll('.slideshow__slide-content--heading .line-child');
          const bodyLines    = item.querySelectorAll('p:not(.subheading) .line-child');
          const buttons      = item.querySelectorAll('.button');

          const tl = gsap.timeline({ paused: true });
          animations[i] = tl;

          if (slideshow.dataset.transition == 'swipe') {
            tl.fromTo(item, {
              clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)'
            }, {
              duration: 0.7,
              clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
            }, "start");
          }

          tl
            .to(bg, { duration: 1, scale: 1 }, "start")
            .fromTo(badge,      { opacity: 0 }, { duration: 0.25, opacity: 1 }, '>-=0.5')
            .fromTo(subheading, { opacity: 0 }, { duration: 0.25, opacity: 1 }, '>-=0.3')
            .from(headingLines, { duration: 0.75, yPercent: 120, stagger: 0.1,  rotation: '2deg' }, '>-=0.3')
            .from(bodyLines,    { duration: 0.5,  yPercent: 120, stagger: 0.02, rotation: '2deg' }, '>-=0.3')
            .fromTo(buttons, { yPercent: 120, rotation: '2deg' }, { duration: 0.5, yPercent: 0, rotation: 0, stagger: 0.1 }, '>-=0.3');
        });
        slideshow.dataset.animationsReady = true;
      }
    }
    animateSlides(i, animations) {
      animations[i].timeScale(1).restart();
    }
    animateReverse(i, animations) {
      animations[i].timeScale(4).reverse();
    }
  }
  customElements.define('slide-show', SlideShow);
}
